import type { Feature } from 'geojson';
import { Level } from '@wemap/geo';
import { Edge, Graph, Vertex } from '@wemap/routers';
import { getLevelProperty } from '@wemap/map';
import type { WemapMap } from '@wemap/map';

import { MULTIROUTERS_ENDPOINT, MULTIROUTERS_ENDPOINT_DEV } from './config.js';
import { loadWheelchairIcons } from './img/wheelchair-icons.js';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from './geoJsonSource.js';

export default class GraphHandler {

    /** @type {Map} */
    _map;

    /** @type {Edge[]} */
    _highways: Edge[] = [];

    _routingBoundsFeature: any;

    _routingIoFeatures: any[] = [];

    _disabledEdges = [];

    _graphGeojson: any;
    environment: 'prod' | 'dev';

    graph: Graph | null = null;

    layersID = [
        'graph',
        'graph-disabled',
        'graph-multilevel',
        'graph-multilevel-disabled',
        'graph-wheelchair-designated',
        'graph-wheelchair-no',
        'routing-bounds',
        'routing-io',
        'graph-arrows'
    ];

    constructor(wemapMap: WemapMap, options: { environment?: 'prod' | 'dev' } = {}) {
        const { environment = 'prod' } = options;
        this.environment = environment;

        const map = this._map = wemapMap.maplibre;

        wemapMap.addSource('graph', {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
        });

        wemapMap.addLayer({
            id: 'graph',
            type: 'line',
            source: 'graph',
            filter: ['all', ['==', ['get', 'multilevel'], false], ['==', ['get', 'disabled'], false]],
            paint: {
                'line-color': [
                    'case',
                        ['!', ['has', 'level']], '#ff9800', // Outdoor edges
                        '#f44336' // Default (indoor) edges
                ],
                'line-width': 2
            }
        }, { indoor: true });

        wemapMap.addLayer({
            id: 'graph-disabled',
            type: 'line',
            source: 'graph',
            filter: ['all', ['==', ['get', 'multilevel'], false], ['==', ['get', 'disabled'], true]],
            paint: {
                'line-color': '#1a0000',
                'line-width': 2
            }
        }, { indoor: true, beforeId: 'graph' });

        wemapMap.addLayer({
            id: 'graph-multilevel',
            type: 'line',
            source: 'graph',
            filter: ['all', ['==', ['get', 'multilevel'], true], ['==', ['get', 'disabled'], false]],
            paint: {
                'line-color': [
                    'case',
                        ['!', ['has', 'level']], '#ff9800', // Outdoor multilevel
                        '#f44336' // Indoor multilevel
                ],
                'line-dasharray': [2, 2],
                'line-width': 2
            }
        }, { indoor: true, beforeId: 'graph-disabled' });

        wemapMap.addLayer({
            id: 'graph-multilevel-disabled',
            type: 'line',
            source: 'graph',
            filter: ['all', ['==', ['get', 'multilevel'], true], ['==', ['get', 'disabled'], true]],
            paint: {
                'line-color': '#3e2723',
                'line-dasharray': [2, 2],
                'line-width': 2
            }
        }, { indoor: true, beforeId: 'graph-multilevel' });

        wemapMap.addLayer({
            id: 'routing-bounds',
            type: 'line',
            source: 'graph',
            filter: ['==', ['get', 'bounds'], true],
            paint: {
                'line-color': '#b71c1c',
                'line-width': 4,
                'line-dasharray': [8, 4]
            }
        }, { indoor: true, beforeId: 'graph-multilevel-disabled' });

        wemapMap.addLayer({
            id: 'routing-io',
            type: 'circle',
            source: 'graph',
            filter: ['==', ['get', 'routingio'], true],
            paint: {
                'circle-color': '#b71c1c',
                'circle-radius': 10
            }
        }, { indoor: true, beforeId: 'routing-bounds' });

        wemapMap.addLayer({
            id: 'graph-arrows',
            type: 'symbol',
            source: 'graph',
            filter: ['==', ['get', 'oneway'], true],
            minzoom: 18,
            layout: {
                'symbol-placement': 'line',
                'symbol-spacing': 20,
                'icon-image': 'arrow-oneway',
                'icon-size': 0.5,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'icon-padding': 0
            },
            paint: {
                'icon-opacity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    17.5, 0,
                    18, 1
                ]
            }
        }, { indoor: true, beforeId: 'routing-io' });

        wemapMap.addLayer({
            id: 'graph-wheelchair-designated',
            type: 'symbol',
            source: 'graph',
            filter: ['==', ['get', 'wheelchair'], "designated"],
            layout: {
                'symbol-placement': 'line',
                'symbol-spacing': 40,
                'icon-image': 'wheelchair-designated',
                'icon-size': 0.6,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'icon-padding': 0
            }
        }, { indoor: true, beforeId: 'graph-arrows' });

        wemapMap.addLayer({
            id: 'graph-wheelchair-no',
            type: 'symbol',
            source: 'graph',
            filter: ['==', ['get', 'wheelchair'], "no"],
            layout: {
                'symbol-placement': 'line',
                'symbol-spacing': 40,
                'icon-image': 'wheelchair-no',
                'icon-size': 0.6,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'icon-padding': 0
            }
        }, { indoor: true, beforeId: 'graph-wheelchair-designated' });

        if (!map.hasImage('arrow-oneway')) {
            void map.loadImage('/img/arrow.png').then(({ data }) => {
                if (!map.hasImage('arrow-oneway')) {
                    map.addImage('arrow-oneway', data);
                }
            });
        }

        loadWheelchairIcons(map);
    }


    getMultiRoutersEndpoint() {
        return this.environment === 'prod' ? MULTIROUTERS_ENDPOINT : MULTIROUTERS_ENDPOINT_DEV;
    }

    async init(graphName: any, useGraphManagement = false) {

        const data = await fetch(`${this.getMultiRoutersEndpoint()}/graph/${graphName}`);
        const graphJson = await data.json();
        this.graph = Graph.fromJson(graphJson);
        this._highways = this.graph.edges;

        this._disabledEdges = await (await fetch(
            `${this.getMultiRoutersEndpoint()}/graph/${graphName}/edges/disabled`
        )).json();


        const routingBounds = graphJson.routingBounds;
        if (routingBounds) {
            this._routingBoundsFeature = {
                type: 'Feature',
                geometry: routingBounds,
                properties: {
                    bounds: true
                }
            };
        }

        this._routingIoFeatures = this.graph.routingBoundsEntryPoints
            .map(node => GraphHandler.vertexToGeojson(node, { routingio: true }));

        if (useGraphManagement) {
            this._initGraphManagement(graphName);
        }
        this._updateFeatures();
    }

    _initGraphManagement(graphName: any) {


        const toggleRequest = async (edgeId: any) => {
            // @ts-expect-error TS(2345): Argument of type 'any' is not assignable to parame... Remove this comment to see the full error message
            const action = this._disabledEdges.includes(edgeId) ? 'enable' : 'disable';
            if (action === 'enable') {
                this._disabledEdges = this._disabledEdges.filter(val => val !== edgeId);
            } else {
                // @ts-expect-error TS(2345): Argument of type 'any' is not assignable to parame... Remove this comment to see the full error message
                this._disabledEdges.push(edgeId);
            }
            this._updateFeatures();

            await fetch(`${this.getMultiRoutersEndpoint()}/graph/${graphName}/edge/${edgeId}/${action}`, {
                method: 'PUT'
            });
        };

        const fnOnClick = (e: any) => {
            e.preventDefault();

            const geojsonProperties = (e.features.length && e.features[0])?.properties;
            const { edgeId } = geojsonProperties;

            toggleRequest(edgeId);
        };

        const map = this._map;
        map.on('click', 'graph', fnOnClick);
        map.on('click', 'graph-disabled', fnOnClick);
        map.on('click', 'graph-multilevel', fnOnClick);
        map.on('click', 'graph-multilevel-disabled', fnOnClick);
        map.on('click', 'graph-wheelchair-designated', fnOnClick);
        map.on('click', 'graph-wheelchair-no', fnOnClick);
    }

    _updateFeatures = () => {
        const highways = this._highways;
        if (!highways.length || !this._map.style) {
            return;
        }

        const features = highways.map((edge: Edge) => GraphHandler.edgeToGeojson(edge, {
            multilevel: Level.isRange(edge.level),
            edgeId: edge.id,
            oneway: edge.properties.isOneway,
            // @ts-expect-error TS(2345): Argument of type 'any' is not assignable to parame... Remove this comment to see the full error message
            disabled: this._disabledEdges.includes(edge.id),
            wheelchair: edge.rawTags?.wheelchair,
            ...getLevelProperty(edge.level)
        })
        );

        features.push(this._routingBoundsFeature);
        features.push(...this._routingIoFeatures);

        setGeoJsonSourceData(this._map, 'graph', {
            type: 'FeatureCollection',
            features
        });
    };

    hideGraph = () => {
        setGeoJsonSourceData(this._map, 'graph', EMPTY_FEATURE_COLLECTION);
    }

    showGraph = () => {
        this._updateFeatures();
    }

    static osmWayToGeojson = (way: any, properties: any): Feature => {
        const coordinates = way.nodes.map((node: any) => [node.coords.lng, node.coords.lat]);
        return {
            type: 'Feature',
            geometry: {
                type: 'LineString',
                coordinates
            },
            properties
        };
    };

    static edgeToGeojson = (edge: Edge, properties: any): Feature => {
        const coordinates = [[edge.vertex1.coords.lng, edge.vertex1.coords.lat], [edge.vertex2.coords.lng, edge.vertex2.coords.lat]];
        return {
            type: 'Feature',
            geometry: {
                type: 'LineString',
                coordinates
            },
            properties
        };
    };

    static osmNodeToGeojson = (node: any, properties: any): Feature => {
        return {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [node.coords.lng, node.coords.lat]
            },
            properties
        };
    };

    static vertexToGeojson = (vertex: Vertex, properties: any): Feature => {
        return {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [vertex.coords.lng, vertex.coords.lat]
            },
            properties
        };
    };

}
